import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import MorphVisual from './MorphVisual';

export default function StudioSection() {
  const equipment = [
    { name: 'DAW', items: ['Pro Tools Studio（2025.12.0）', 'Nuendo 15'], updatedAt: '2026年9月22日' },
    { name: 'Audio interface', items: ['RME Fireface UCX', 'RME Babyface', 'RME Quad MiC'], updatedAt: null },
    { name: 'Monitor speaker', items: ['Neumann KH 80 DSP'], updatedAt: null },
    { name: 'Equipment / 機材', items: [
      'SENNHEISER MKH 416-P48U3',
      'SENNHEISER MD441-U×２',
      'Sennheiser PROFILE WIRELESS 2-CH',
      'Deity Microphones Lapelマイク IP57',
      'ZOOM F8n-Pro',
      'ZOOM H5Studio',
      'PROTECH FS-205 2chハイパーリミッターユニット',
      'BLUTTY EB55',
      'Marantz SOUND SHIELD',
      'Sachtler SN602 + SN605 イヤーゴナイザー L（ハーネス付属）',
      'SUPERIOR In-ear monitor',
      'SONY α SLT-A55V',
      'Yamaki VUM-D208'
    ], updatedAt: '2026年10月5日' }
  ];

  const updateHistory: { date: string; content: string; link?: string }[] = [
    { date: '2026年10月5日', content: 'Equipment に SUPERIOR In-ear monitor / SONY α SLT-A55V / Yamaki VUM-D208 を追加' },
    { date: '2026年9月28日', content: 'スタジオが完成 — 特集ページ「WakuWaku Studio. 工事記録」を公開', link: '/wakuwaku-studio-log' },
    { date: '2026年9月22日', content: 'DAW を Nuendo 15 に更新' },
    { date: '2026年9月12日', content: 'Equipment に Sachtler SN602 + SN605 イヤーゴナイザー L（ハーネス付属）を追加' },
    { date: '2026年7月4日', content: 'Equipment に ZOOM H5Studio を追加' },
    { date: '2026年3月24日', content: 'Equipment に Deity Microphones Lapelマイク IP57 を追加 / DAW を Pro Tools Studio（2025.12.0）に更新' },
    { date: '2026年3月24日（初回）', content: 'DAW を Nuendo 14 追加 / 設備一覧を公開' },
  ];

  return (
    <section id="studio" className="py-24 md:py-32 bg-site-mid">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <Reveal>
          <div className="text-center mb-20">
            <p className="eyebrow text-teal-400 text-sm md:text-base mb-4">The Studio</p>
            <h2 className="text-3xl md:text-5xl font-mincho text-white mb-6">スタジオ紹介</h2>
            <div className="w-20 h-[3px] bg-teal-400 mx-auto rounded-full"></div>
          </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="bg-white/[0.03] p-8 rounded-xl border border-white/10">
                <h3 className="text-2xl font-mincho text-white mb-4">
                  <i className="ri-home-4-line text-teal-400 mr-2"></i>
                  スタジオについて
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  一つ一つの音が持つ魅力を最大限に引き出すために設計された空間です。プロフェッショナルな音の演出（効果音）や、映像に「感動」や「臨場感」を吹き込む、音の最終仕上げに特化しています。
                </p>
              </div>
            </div>

            <MorphVisual className="border border-white/10">
              <img
                src="/images/backgrounds/studio-neumann-real.jpg"
                alt="WakuWakuスタジオ"
                className="w-full h-auto object-cover object-top"
              />
            </MorphVisual>
          </div>

          <div className="mt-16">
            {/* 更新通知バナー */}
            <div className="mb-8 bg-gradient-to-r from-teal-600 to-teal-500 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 bg-black/20 rounded-full shrink-0">
                  <i className="ri-notification-3-fill text-white text-xl"></i>
                </span>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-yellow-400 text-yellow-900 text-xs font-black px-2.5 py-0.5 rounded-full animate-pulse whitespace-nowrap">NEW UPDATE</span>
                    <span className="text-white font-bold text-sm">2026年9月28日 更新</span>
                  </div>
                  <p className="text-teal-100 text-xs mt-0.5">スタジオが完成しました — 特集ページを公開中</p>
                  <Link
                    to="/wakuwaku-studio-log"
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 px-3 py-1 rounded-full transition-colors whitespace-nowrap"
                  >
                    スタジオ特集ページを見る
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
              <details className="group cursor-pointer">
                <summary className="text-white text-xs font-semibold flex items-center gap-1 whitespace-nowrap list-none cursor-pointer select-none">
                  <i className="ri-history-line"></i>
                  更新履歴を見る
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="mt-3 bg-black/20 rounded-lg p-3 min-w-[280px]">
                  {updateHistory.map((h, i) => (
                    <div key={i} className={`flex gap-3 text-xs ${i < updateHistory.length - 1 ? 'mb-2 pb-2 border-b border-white/20' : ''}`}>
                      <span className="text-yellow-300 font-bold whitespace-nowrap">{h.date}</span>
                      {h.link ? (
                        <Link to={h.link} className="text-teal-200 underline underline-offset-2 hover:text-white transition-colors">
                          {h.content} <i className="ri-external-link-line"></i>
                        </Link>
                      ) : (
                        <span className="text-teal-100">{h.content}</span>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            </div>

            <div className="flex items-center justify-center gap-3 mb-8">
              <h3 className="text-2xl font-mincho text-white">主な設備</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {equipment.map((category, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-6 transition-all relative ${
                    category.updatedAt
                      ? 'bg-teal-500/10 border-2 border-teal-400/40'
                      : 'bg-white/[0.03] border border-white/10'
                  }`}
                >
                  {category.updatedAt && (
                    <span className="absolute -top-3 left-4 bg-teal-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                      <i className="ri-refresh-line text-xs"></i>
                      UPDATED
                    </span>
                  )}
                  <h4 className={`text-lg font-medium mb-3 ${category.updatedAt ? 'text-teal-200' : 'text-white'}`}>
                    {category.name}
                  </h4>
                  <div className="text-zinc-400 flex flex-col gap-1">
                    {category.items.map((item, itemIndex) => (
                      <span key={itemIndex} className="inline-flex items-start">
                        <i className="ri-check-line text-teal-400 mr-1 text-sm mt-0.5 shrink-0"></i>
                        <span className="text-sm">{item}</span>
                      </span>
                    ))}
                  </div>
                  {category.updatedAt && (
                    <p className="mt-3 text-xs text-teal-300 font-medium border-t border-teal-400/20 pt-2">
                      <i className="ri-time-line mr-1"></i>{category.updatedAt} 更新
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
